/** The stable key's shape: a language code, a dot, a snake_case entry name. A key can never carry text. */
export const VOICEMAIL_PHRASE_KEY_PATTERN = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*\.[a-z][a-z0-9_]*$/u;
/** The signal each set field gives when it matches. */
export const VOICEMAIL_PHRASE_SIGNAL = Object.freeze({
    phrase: "voicemail_phrase",
    announcement: "announcement_phrase",
    word: "voicemail_word",
    personGreeting: "person_phrase",
});
/**
 * Folds a text for matching: NFC, lower case, combining marks removed, final sigma as σ, every punctuation mark and
 * symbol a space, white space collapsed and trimmed.
 */
export function foldVoicemailText(text) {
    return text
        .normalize("NFC")
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{M}+/gu, "")
        .normalize("NFC")
        .replace(/ς/gu, "σ")
        .replace(/[\p{P}\p{S}]+/gu, " ")
        .replace(/\s+/gu, " ")
        .trim();
}
/**
 * Folds every set's entries once. Refuses an entry whose key is not a stable key, whose key does not start with its
 * set's language, whose key is used twice, or whose text folds to nothing — a pack fault that must stop start-up, not a
 * phrase that silently never matches.
 *
 * @throws {Error} naming the key, never the text.
 */
export function voicemailPhraseCatalogueOf(sets) {
    const entry = [];
    const seen = new Set();
    for (const set of sets) {
        for (const field of Object.keys(VOICEMAIL_PHRASE_SIGNAL)) {
            for (const one of set[field]) {
                if (!VOICEMAIL_PHRASE_KEY_PATTERN.test(one.key))
                    throw new Error(`voicemail phrase key "${one.key}" is not <language>.<entry>`);
                if (!one.key.startsWith(`${set.languageCode}.`)) {
                    throw new Error(`voicemail phrase key "${one.key}" does not belong to the "${set.languageCode}" set`);
                }
                if (seen.has(one.key))
                    throw new Error(`voicemail phrase key "${one.key}" is used twice`);
                seen.add(one.key);
                const folded = foldVoicemailText(one.text);
                if (folded === "")
                    throw new Error(`voicemail phrase "${one.key}" folds to nothing`);
                entry.push({ signal: VOICEMAIL_PHRASE_SIGNAL[field], key: one.key, folded });
            }
        }
    }
    return Object.freeze({ entry: Object.freeze(entry) });
}
/**
 * Every phrase the utterance's text so far (a partial or a final) matches, in catalogue order: a mailbox, announcement
 * or weak-word entry when its folded text is contained in the folded utterance on word boundaries; a person greeting only
 * when the whole folded utterance equals it.
 */
export function voicemailPhraseMatchOf(text, catalogue) {
    const folded = foldVoicemailText(text);
    if (folded === "")
        return [];
    const padded = ` ${folded} `;
    const match = [];
    for (const one of catalogue.entry) {
        const hit = one.signal === "person_phrase" ? folded === one.folded : padded.includes(` ${one.folded} `);
        if (hit)
            match.push({ signal: one.signal, phraseKey: one.key });
    }
    return match;
}
//# sourceMappingURL=phrase.js.map